import React, { useState } from "react";
import { compose, graphql } from "react-apollo";
import Drawer from "../../../hoc/Drawer";
import { GET_LEAD_ON_EDIT } from "../../../graphql/queries";
import CustomButton from "../../../components/custom-button";
import CreateEditLeadWithMutation from "../forms/create-edit-lead-container";

/**
 * This component renders the edit lead drawer (from table or details).
 * Same pattern as Edit Prospect: close drawer, then if red flag was updated call update().
 *
 * @param {object} props.leadData - lead when called from details page
 * @param {object} props.data - graphql result when called from table (EditLeadViewWithData)
 * @param {function} props.close - closes the drawer (table only)
 * @param {boolean} props.show - whether drawer is shown (table only)
 * @param {boolean} props.textButton - true when used from details (render Edit button that opens drawer)
 * @param {number} props.id - lead id
 * @param {function} props.update - called after submit or when closing after flag update
 */
const EditLeadView = (props) => {
  const { leadData, data, close, show, textButton, id, update } = props;
  const [showSideBar, setShowSideBar] = useState(false);
  const [redFlagUpdated, setRedFlagUpdated] = useState(false);

  const drawerShow = textButton ? showSideBar : show;
  const lead = textButton ? leadData : data?.getLeads?.Leads?.[0];

  const toggleShowDrawer = () => {
    if (textButton) {
      setShowSideBar(false);
    } else {
      close && close();
    }
    if (redFlagUpdated && update) {
      update();
      setRedFlagUpdated(false);
    }
  };

  const onSubmit = (message) => {
    setShowSideBar(false);
    if (!textButton) close && close();
    update && update(message);
  };

  const formatData = (leadDataToFormat) => {
    if (!leadDataToFormat) return null;
    const {
      first_name_1,
      last_name_1,
      primary_email_1,
      cell_phone_1,
      secondary_emails_1,
      first_name_2,
      last_name_2,
      primary_email_2,
      cell_phone_2,
      secondary_emails_2,
      city,
      state,
      zip,
      street_address,
      realtor,
      customer_origination_area,
      id: leadId,
      osc_notes,
      primary_email_1_status,
      primary_email_2_status,
      red_flag,
      divisions,
      communities,
      next_steps,
      other_notes,
      quality_score,
      engagement_score,
      lead_classification_filters,
    } = leadDataToFormat;

    const dataToReturn = {
      first_name_1,
      last_name_1,
      primary_email_1,
      disable_primary_email_1: !primary_email_1_status,
      cell_phone_1,
      secondary_emails_1,
      first_name_2,
      last_name_2,
      primary_email_2,
      disable_primary_email_2: !primary_email_2_status,
      cell_phone_2,
      secondary_emails_2,
      city,
      customer_origination_area_id: (customer_origination_area && customer_origination_area.id) || undefined,
      state_id: state && state.id ? state.id : undefined,
      zip,
      realtor_id: realtor ? realtor.id : undefined,
      street_address,
      osc_notes,
      id: leadId,
      red_flag: red_flag && red_flag.description,
    };

    dataToReturn.Lead = {
      divisions: divisions && divisions.map((item) => item.id),
      communities: communities && communities.filter((item) => item.is_active).map((item) => item.id),
      lead_classification_filter_ids: lead_classification_filters && lead_classification_filters.map((item) => item.id),
      next_step: next_steps?.[0]?.note ?? '',
      next_steps: next_steps ?? [],
      other_notes,
      quality_score: quality_score ? quality_score : undefined,
      engagement_score: engagement_score ? engagement_score : undefined,
    };
    dataToReturn.communities = leadDataToFormat.communities;
    return dataToReturn;
  };

  const inActiveCommunities = lead ? lead.communities?.filter((item) => !item.is_active) ?? [] : [];

  return (
    <div className="edit-button">
      {textButton && (
        <CustomButton
          size="sm"
          color="dark"
          btnValue={props.btnValue}
          onClick={() => setShowSideBar(true)}
        />
      )}
      <Drawer show={drawerShow} toggleSideBar={toggleShowDrawer}>
        {lead ? (
          <CreateEditLeadWithMutation
            close={toggleShowDrawer}
            data={formatData(lead)}
            id={id}
            editForm
            update={onSubmit}
            isDetails={props.isDetails}
            history={props.history}
            inActiveCommunities={inActiveCommunities}
            refetchEditData={() => setRedFlagUpdated(true)}
          />
        ) : null}
      </Drawer>
    </div>
  );
};

export default EditLeadView;

export const EditLeadViewWithData = compose(
  graphql(GET_LEAD_ON_EDIT, {
    options: (props) => ({
      variables: { filter: { id: props.id } },
      fetchPolicy: "network-only",
    }),
  })
)(EditLeadView);
